import React, { useState, useEffect, useCallback } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import { useGamification } from '../contexts/GamificationContext';
import {
  ArrowLeft, Dumbbell, TrendingUp, Trash2, ChevronRight, Activity, Target,
  CheckCircle2, Play, Check, Calendar, Zap, Award, Battery, ArrowUpDown
} from 'lucide-react';
import { calculateDailyPoints, isMealsCompleted, isSleepCompleted } from '../utils/dailyProgressPoints';

interface WorkoutExercise {
  name: string;
  sets: number;
  reps: string;
  muscle_group: string;
  notes?: string;
  suggestions?: string[];
  tips?: string[];
}

interface WorkoutPlan {
  name: string;
  description: string;
  icon: React.ElementType;
  days: {
    [key: string]: WorkoutExercise[];
  };
}

interface UserWorkoutLog {
  id: string;
  user_id: string;
  exercise_name: string;
  load: number;
  reps: number;
  sets: number;
  date: string;
  date_formatted: string;
  time_formatted: string;
}

interface HistoricoCarga {
  id: string;
  exercicio: string;
  carga: number;
  repeticoes: number;
  series: number;
  data: string;
  horario: string;
}




const BG_DARK = '#0D0D0D';
const BORDER_COLOR = 'rgba(255, 255, 255, 0.1)';

const workoutTemplates: Record<string, WorkoutPlan> = {
  abc: {
    name: 'ABC - Hipertrofia',
    description: 'Divisão clássica para ganho de massa muscular. Foco em grupos musculares sinérgicos.',
    icon: Dumbbell,
    days: {
      'Peito e Tríceps': [
        { name: 'Supino Reto com Barra', sets: 4, reps: '8-12', muscle_group: 'Peito', suggestions: ['Supino com Halteres', 'Supino na Máquina'], tips: ['Mantenha os pés firmes no chão', 'Desça a barra até o peito', 'Não trave os cotovelos no topo'] },
        { name: 'Supino Inclinado com Halteres', sets: 3, reps: '8-12', muscle_group: 'Peito', suggestions: ['Supino Inclinado com Barra', 'Crucifixo Inclinado'], tips: ['Ajuste o banco a 30-45 graus', 'Foque na parte superior do peito'] },
        { name: 'Crucifixo na Máquina (Peck Deck)', sets: 3, reps: '10-15', muscle_group: 'Peito', suggestions: ['Crucifixo com Halteres', 'Crossover na Polia'], tips: ['Controle o movimento', 'Sinta o alongamento do peitoral'] },
        { name: 'Tríceps na Polia com Corda', sets: 4, reps: '10-15', muscle_group: 'Tríceps', suggestions: ['Tríceps na Polia com Barra', 'Tríceps Testa'], tips: ['Mantenha os cotovelos parados', 'Estenda completamente o braço'] },
        { name: 'Tríceps Francês com Halter', sets: 3, reps: '10-15', muscle_group: 'Tríceps', suggestions: ['Tríceps Testa', 'Mergulho no Banco'], tips: ['Cuidado com a lombar', 'Amplitude total de movimento'] }
      ],
      'Costas e Bíceps': [
        { name: 'Puxada Alta na Polia (Frente)', sets: 4, reps: '8-12', muscle_group: 'Costas', suggestions: ['Barra Fixa', 'Graviton'], tips: ['Puxe com os cotovelos', 'Mantenha o peito estufado'] },
        { name: 'Remada Baixa no Cabo (Triângulo)', sets: 4, reps: '8-12', muscle_group: 'Costas', suggestions: ['Remada Curvada com Barra', 'Remada Cavalinho'], tips: ['Mantenha a coluna reta', 'Puxe em direção ao quadril'] },
        { name: 'Rosca Direta com Barra', sets: 4, reps: '10-15', muscle_group: 'Bíceps', suggestions: ['Rosca com Halteres', 'Rosca Scott'], tips: ['Não balance o corpo', 'Controle a descida'] },
        { name: 'Rosca Martelo com Halteres', sets: 3, reps: '10-15', muscle_group: 'Bíceps', suggestions: ['Rosca com Corda na Polia', 'Rosca Inversa'], tips: ['Foco no braquial', 'Pegada neutra'] }
      ],
      'Pernas e Ombros': [
        { name: 'Agachamento Livre com Barra', sets: 4, reps: '8-12', muscle_group: 'Pernas', suggestions: ['Leg Press 45º', 'Agachamento Hack'], tips: ['Pés na largura dos ombros', 'Joelho acompanha a ponta do pé', 'Mantenha a coluna neutra'] },
        { name: 'Leg Press 45º', sets: 4, reps: '10-15', muscle_group: 'Pernas', suggestions: ['Agachamento Búlgaro', 'Afundo com Halteres'], tips: ['Não trave os joelhos', 'Empurre com o calcanhar'] },
        { name: 'Cadeira Extensora', sets: 3, reps: '12-15', muscle_group: 'Pernas', suggestions: ['Sissy Squat'], tips: ['Segure 1s no topo', 'Controle a descida'] },
        { name: 'Desenvolvimento com Halteres', sets: 4, reps: '8-12', muscle_group: 'Ombros', suggestions: ['Desenvolvimento Máquina', 'Elevação Frontal'], tips: ['Não arqueie as costas', 'Empurre acima da cabeça'] },
        { name: 'Elevação Lateral com Halteres', sets: 4, reps: '12-15', muscle_group: 'Ombros', suggestions: ['Elevação Lateral na Polia'], tips: ['Cotovelos levemente flexionados', 'Não suba acima do ombro'] }
      ]
    }
  },
  upper_lower: {
    name: 'Upper / Lower',
    description: 'Foco em força e progressão de carga. Ideal para intermediários.',
    icon: ArrowUpDown,
    days: {
      'Upper': [
        { name: 'Supino Reto com Barra', sets: 4, reps: '6-8', muscle_group: 'Peito', suggestions: ['Supino com Halteres', 'Supino na Máquina'], tips: ['Foco na carga com boa forma', 'Desça controlado'] },
        { name: 'Remada Curvada com Barra', sets: 4, reps: '6-8', muscle_group: 'Costas', suggestions: ['Remada Cavalinho', 'Serrote (Remada Unilateral)'], tips: ['Mantenha a coluna neutra', 'Puxe para o quadril'] },
        { name: 'Desenvolvimento Militar com Barra', sets: 3, reps: '8-10', muscle_group: 'Ombros', suggestions: ['Desenvolvimento com Halteres'], tips: ['Estabilize o core', 'Não use impulso das pernas'] },
        { name: 'Barra Fixa (Pull-up)', sets: 3, reps: 'Falha', muscle_group: 'Costas', suggestions: ['Puxada Alta na Polia'], tips: ['Amplitude total', 'Controle a descida'] }
      ],
      'Lower': [
        { name: 'Agachamento Livre com Barra', sets: 4, reps: '6-8', muscle_group: 'Pernas', suggestions: ['Agachamento Hack'], tips: ['Quebre a paralela se possível', 'Respire fundo antes de descer'] },
        { name: 'Stiff com Barra', sets: 4, reps: '8-10', muscle_group: 'Pernas', suggestions: ['Levantamento Terra Romeno (RDL)', 'Mesa Flexora'], tips: ['Mantenha a coluna reta', 'Sinta o posterior alongar'] },
        { name: 'Leg Press 45º', sets: 3, reps: '10-12', muscle_group: 'Pernas', suggestions: ['Agachamento Búlgaro'], tips: ['Não tire o quadril do banco', 'Empurre com o calcanhar'] },
        { name: 'Panturrilha em Pé na Máquina', sets: 4, reps: '15-20', muscle_group: 'Pernas', suggestions: ['Panturrilha Sentado'], tips: ['Pausa no topo e embaixo', 'Amplitude máxima'] }
      ]
    }
  },
  full_body: {
    name: 'Full Body 3x',
    description: 'Treine o corpo todo 3x na semana. Ótimo para iniciantes ou quem tem pouco tempo.',
    icon: Activity,
    days: {
      'Treino A': [
        { name: 'Agachamento Livre com Barra', sets: 3, reps: '10', muscle_group: 'Pernas', suggestions: ['Leg Press 45º'], tips: ['Coluna neutra', 'Pés firmes'] },
        { name: 'Supino Reto com Barra', sets: 3, reps: '10', muscle_group: 'Peito', suggestions: ['Flexão de Braço'], tips: ['Cotovelos a 45 graus', 'Peito estufado'] },
        { name: 'Remada Curvada com Barra', sets: 3, reps: '10', muscle_group: 'Costas', suggestions: ['Puxada Alta na Polia'], tips: ['Puxe com as costas, não só braços'] },
        { name: 'Desenvolvimento Militar com Barra', sets: 3, reps: '10', muscle_group: 'Ombros', suggestions: ['Elevação Lateral com Halteres'], tips: ['Não arqueie a lombar'] }
      ]
    }
  },
  old_school: {
    name: 'Old School',
    description: 'Estilo Golden Era. Volume alto, foco em estética e proporção.',
    icon: Dumbbell,
    days: {
      'Peito e Costas': [
        { name: 'Supino Reto com Barra', sets: 5, reps: '8-10', muscle_group: 'Peito', suggestions: ['Supino com Halteres'], tips: ['Movimento explosivo na subida', 'Controle na descida'] },
        { name: 'Remada Curvada com Barra', sets: 5, reps: '8-10', muscle_group: 'Costas', suggestions: ['Remada Baixa com Barra T (T-Bar)'], tips: ['Tronco paralelo ao chão', 'Alongue bem as dorsais'] },
        { name: 'Supino Inclinado com Barra', sets: 4, reps: '10-12', muscle_group: 'Peito', suggestions: ['Crucifixo Inclinado'], tips: ['Foco na parte superior', 'Não toque os halteres no topo'] },
        { name: 'Puxada Alta na Polia', sets: 4, reps: '10-12', muscle_group: 'Costas', suggestions: ['Barra Fixa'], tips: ['Pegada larga', 'Traga a barra no peito'] },
        { name: 'Crucifixo com Halteres', sets: 3, reps: '12-15', muscle_group: 'Peito', suggestions: ['Peck Deck'], tips: ['Abra bem o peito', 'Imagine abraçar uma árvore'] },
        { name: 'Remada Baixa no Cabo', sets: 3, reps: '12-15', muscle_group: 'Costas', suggestions: ['Remada Unilateral'], tips: ['Coluna reta', 'Puxe na direção do umbigo'] }
      ],
      'Ombros e Braços': [
        { name: 'Desenvolvimento Militar com Barra', sets: 4, reps: '8-10', muscle_group: 'Ombros', suggestions: ['Desenvolvimento com Halteres'], tips: ['Barra na frente da cabeça', 'Trave o abdômen'] },
        { name: 'Elevação Lateral com Halteres', sets: 4, reps: '12-15', muscle_group: 'Ombros', suggestions: ['Elevação Lateral na Polia'], tips: ['Foco no deltoide lateral', 'Não use impulso'] },
        { name: 'Rosca Direta com Barra', sets: 4, reps: '10-12', muscle_group: 'Bíceps', suggestions: ['Rosca Barra W'], tips: ['Cotovelos fixos', 'Contração de pico'] },
        { name: 'Tríceps Testa com Barra W', sets: 4, reps: '10-12', muscle_group: 'Tríceps', suggestions: ['Tríceps Francês'], tips: ['Desça a barra na testa', 'Cotovelos fechados'] },
        { name: 'Rosca Martelo com Halteres', sets: 3, reps: '12-15', muscle_group: 'Bíceps', suggestions: ['Rosca na Polia com Corda'], tips: ['Foco no braquial', 'Movimento controlado'] },
        { name: 'Tríceps na Polia com Corda', sets: 3, reps: '12-15', muscle_group: 'Tríceps', suggestions: ['Mergulho no Banco'], tips: ['Abra a corda no final', 'Esmague o tríceps'] }
      ],
      'Pernas Completo': [
        { name: 'Agachamento Livre com Barra', sets: 5, reps: '8-10', muscle_group: 'Pernas', suggestions: ['Agachamento Frontal'], tips: ['Amplitude total', 'Olhar para frente'] },
        { name: 'Leg Press 45º', sets: 4, reps: '12-15', muscle_group: 'Pernas', suggestions: ['Hack Squat'], tips: ['Pés mais baixos para quadríceps', 'Não trave o joelho'] },
        { name: 'Cadeira Extensora', sets: 4, reps: '15-20', muscle_group: 'Pernas', suggestions: ['Sissy Squat'], tips: ['Segure 1s no topo', 'Queime o quadríceps'] },
        { name: 'Stiff com Barra', sets: 4, reps: '10-12', muscle_group: 'Pernas', suggestions: ['Mesa Flexora'], tips: ['Coluna reta', 'Sinta o posterior'] },
        { name: 'Panturrilha em Pé na Máquina', sets: 5, reps: '15-20', muscle_group: 'Pernas', suggestions: ['Panturrilha Sentado'], tips: ['Amplitude máxima', 'Sem pressa'] }
      ]
    }
  },
  low_volume: {
    name: 'Low Volume',
    description: 'Alta intensidade (HIT). Poucas séries, até a falha total.',
    icon: Battery,
    days: {
      'Treino A': [
        { name: 'Supino Inclinado com Barra', sets: 2, reps: '6-10', muscle_group: 'Peito', suggestions: ['Supino Reto com Barra'], tips: ['Falha total na última série', 'Cadência lenta (4-2-4)'] },
        { name: 'Elevação Lateral com Halteres', sets: 2, reps: '10-12', muscle_group: 'Ombros', suggestions: ['Desenvolvimento com Halteres'], tips: ['Controle absoluto', 'Sem roubar'] },
        { name: 'Tríceps Testa com Barra W', sets: 2, reps: '8-10', muscle_group: 'Tríceps', suggestions: ['Tríceps na Polia'], tips: ['Isolamento total', 'Falha concêntrica'] },
        { name: 'Puxada Alta Supinada (Chin-up)', sets: 2, reps: '6-10', muscle_group: 'Costas', suggestions: ['Barra Fixa'], tips: ['Puxe com força', 'Segure a negativa'] },
        { name: 'Levantamento Terra com Barra', sets: 1, reps: '5-8', muscle_group: 'Costas', suggestions: ['Remada Curvada com Barra'], tips: ['Técnica perfeita', 'Apenas uma série de trabalho'] }
      ],
      'Treino B': [
        { name: 'Agachamento Livre com Barra', sets: 2, reps: '6-10', muscle_group: 'Pernas', suggestions: ['Leg Press 45º'], tips: ['Desça devagar', 'Exploda na subida'] },
        { name: 'Leg Press 45º', sets: 1, reps: '10-15', muscle_group: 'Pernas', suggestions: ['Cadeira Extensora'], tips: ['Até a falha absoluta', 'Cuidado com a segurança'] },
        { name: 'Stiff com Barra', sets: 2, reps: '8-12', muscle_group: 'Pernas', suggestions: ['Mesa Flexora'], tips: ['Alongamento máximo', 'Contração controlada'] },
        { name: 'Panturrilha em Pé', sets: 2, reps: '12-15', muscle_group: 'Pernas', suggestions: ['Panturrilha Sentado'], tips: ['Pausa de 2s embaixo', 'Pausa de 2s em cima'] },
        { name: 'Rosca Direta com Barra', sets: 2, reps: '8-10', muscle_group: 'Bíceps', suggestions: ['Rosca Scott'], tips: ['Não use as costas', 'Esmague o bíceps'] }
      ]
    }
  },
  torso_limbs: {
    name: 'Torso / Limbs',
    description: 'Divisão Tronco e Membros. Excelente para frequência e recuperação.',
    icon: Calendar,
    days: {
      'Torso (Tronco)': [
        { name: 'Supino Reto com Barra', sets: 4, reps: '8-10', muscle_group: 'Peito', suggestions: ['Supino com Halteres'], tips: ['Estabilidade escapular', 'Pés firmes'] },
        { name: 'Remada Curvada com Barra', sets: 4, reps: '8-10', muscle_group: 'Costas', suggestions: ['Remada na Máquina'], tips: ['Cotovelos próximos ao corpo', 'Coluna neutra'] },
        { name: 'Desenvolvimento com Halteres', sets: 3, reps: '10-12', muscle_group: 'Ombros', suggestions: ['Elevação Lateral com Halteres'], tips: ['Não estenda demais os cotovelos', 'Controle'] },
        { name: 'Puxada Alta na Polia', sets: 3, reps: '10-12', muscle_group: 'Costas', suggestions: ['Barra Fixa'], tips: ['Foco nas dorsais', 'Não balance'] },
        { name: 'Elevação Lateral com Halteres', sets: 3, reps: '12-15', muscle_group: 'Ombros', suggestions: ['Crucifixo Inverso'], tips: ['Pulsos firmes', 'Foco no ombro lateral'] }
      ],
      'Limbs (Membros)': [
        { name: 'Agachamento Livre com Barra', sets: 4, reps: '8-10', muscle_group: 'Pernas', suggestions: ['Leg Press 45º'], tips: ['Profundidade consistente', 'Respire fundo'] },
        { name: 'Stiff com Barra', sets: 4, reps: '10-12', muscle_group: 'Pernas', suggestions: ['Mesa Flexora'], tips: ['Quadril para trás', 'Joelhos semi-flexionados'] },
        { name: 'Rosca Direta com Barra', sets: 3, reps: '10-12', muscle_group: 'Bíceps', suggestions: ['Rosca Martelo com Halteres'], tips: ['Cotovelos ao lado do corpo', 'Sem balanço'] },
        { name: 'Tríceps Testa com Barra W', sets: 3, reps: '10-12', muscle_group: 'Tríceps', suggestions: ['Tríceps na Polia com Corda'], tips: ['Estabilidade', 'Foco na extensão'] },
        { name: 'Panturrilha na Máquina', sets: 4, reps: '15-20', muscle_group: 'Pernas', suggestions: ['Panturrilha Sentado'], tips: ['Amplitude total', 'Controle'] }
      ]
    }
  }
};

export default function Workouts() {
  const { user } = useAuth();
  // const { addXP } = useGamification();
  const [activePlanId, setActivePlanId] = useState<string | null>(localStorage.getItem('activePlanId'));
  const [activeWorkoutDay, setActiveWorkoutDay] = useState<string | null>(null);
  const [currentView, setCurrentView] = useState<'main' | 'detail' | 'history' | 'register' | 'individual'>('main');
  const [selectedWorkoutId, setSelectedWorkoutId] = useState<string | null>(null);
  const [userLogs, setUserLogs] = useState<UserWorkoutLog[]>([]);
  const [historicoCargas, setHistoricoCargas] = useState<HistoricoCarga[]>([]);
  interface DailyProgressItem {
    id: string;
    user_id: string;
    date: string;
    water_ml: number;
    workout_completed: boolean;
    sleep_hours: number | null;
    meals_logged: number;
    inserted_at: string;
    updated_at: string;
  }
  const [dailyProgress, setDailyProgress] = useState<DailyProgressItem[]>([]);

  const loadUserLogs = useCallback(async () => {
    if (!user) return;

    // Fetch workout logs (for history and streak fallback)
    const { data: logs } = await supabase.from('user_workout_logs').select('*').eq('user_id', user.id).order('date', { ascending: false });
    if (logs) {
      setUserLogs(logs as UserWorkoutLog[]);
      setHistoricoCargas(logs.map((log: UserWorkoutLog) => ({
        id: log.id, exercicio: log.exercise_name, carga: log.load, repeticoes: log.reps, series: log.sets, data: log.date_formatted, horario: log.time_formatted
      })));
    }

    // Fetch daily progress (for consistent weekly stats)
    const { data: progress } = await supabase.from('daily_progress').select('*').eq('user_id', user.id).order('date', { ascending: false });
    if (progress) {
      setDailyProgress(progress);
    }
  }, [user]);

  useEffect(() => {
    loadUserLogs();
  }, [user, loadUserLogs]);


  const handleActivatePlan = (id: string) => { setActivePlanId(id); localStorage.setItem('activePlanId', id); };
  const handleStartWorkout = (dayName: string) => { setActiveWorkoutDay(dayName); setCurrentView('individual'); };

  // Logic to determine the next workout day
  const getNextWorkout = () => {
    if (!activePlanId) return null;
    const plan = workoutTemplates[activePlanId];
    if (!plan) return null;
    const days = Object.keys(plan.days);

    if (userLogs.length > 0) {
      const lastLog = userLogs[0];
      for (let i = 0; i < days.length; i++) {
        const day = days[i];
        const exercises = plan.days[day];
        if (exercises.some(e => e.name === lastLog.exercise_name)) {
          const nextIndex = (i + 1) % days.length;
          return days[nextIndex];
        }
      }
    }
    return days[0];
  };

  const nextWorkoutDay = getNextWorkout();

  const getNextNextWorkout = () => {
    if (!activePlanId || !nextWorkoutDay) return null;
    const plan = workoutTemplates[activePlanId];
    if (!plan) return null;
    const days = Object.keys(plan.days);
    const currentIndex = days.indexOf(nextWorkoutDay);
    if (currentIndex === -1) return days[0];
    const nextIndex = (currentIndex + 1) % days.length;
    return days[nextIndex];
  };
  const nextNextWorkoutDay = getNextNextWorkout();

  const isLegDay = nextWorkoutDay ? (nextWorkoutDay.toLowerCase().includes('perna') || nextWorkoutDay.toLowerCase().includes('leg')) : false;

  const getStreakAndWeeklyStatus = () => {
    // Use daily_progress as the source of truth for completed workouts
    const completedDates = new Set(
      dailyProgress
        .filter(p => p.workout_completed)
        .map(p => p.date)
    );

    // Fallback to userLogs if daily_progress is empty (migration support)
    if (completedDates.size === 0 && userLogs.length > 0) {
      userLogs.forEach(log => completedDates.add(log.date.split('T')[0]));
    }

    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = yesterday.toISOString().split('T')[0];

    let streak = 0;
    const checkDate = completedDates.has(todayStr) ? new Date(today) : new Date(yesterday);

    if (completedDates.has(todayStr) || completedDates.has(yesterdayStr)) {
      while (true) {
        const dStr = checkDate.toISOString().split('T')[0];
        if (completedDates.has(dStr)) {
          streak++;
          checkDate.setDate(checkDate.getDate() - 1);
        } else {
          break;
        }
      }
    }

    const weekDays = [];
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay());

    const weekDayNames = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];

    for (let i = 0; i < 7; i++) {
      const d = new Date(startOfWeek);
      d.setDate(startOfWeek.getDate() + i);
      const dStr = d.toISOString().split('T')[0];
      const isDone = completedDates.has(dStr);
      const isToday = dStr === todayStr;
      weekDays.push({ day: weekDayNames[i], isDone, isToday });
    }

    return { streak, weekDays };
  };

  const { streak, weekDays } = getStreakAndWeeklyStatus();

  const motivationalPhrases = [
    "Sem desculpas hoje.",
    "Mantenha o ritmo.",
    "Consistência vence talento."
  ];
  const motivationPhrase = motivationalPhrases[new Date().getDate() % motivationalPhrases.length];

  const exerciseCount = activePlanId && nextWorkoutDay ? workoutTemplates[activePlanId].days[nextWorkoutDay]?.length || 0 : 0;

  if (currentView === 'detail' && selectedWorkoutId) return <WorkoutDetail workout={workoutTemplates[selectedWorkoutId]} workoutId={selectedWorkoutId} isActive={activePlanId === selectedWorkoutId} onActivate={handleActivatePlan} onBack={() => setCurrentView('main')} userLogs={userLogs} />;
  if (currentView === 'history' && user) return <LoadHistory onBack={() => setCurrentView('main')} historicoCargas={historicoCargas} setHistoricoCargas={setHistoricoCargas} user={user} />;
  if (currentView === 'register') return <RegisterLoad onBack={() => setCurrentView('main')} onNavigateToHistory={() => setCurrentView('history')} setHistoricoCargas={setHistoricoCargas} />;
  if (currentView === 'individual' && activePlanId && activeWorkoutDay) return <IndividualWorkoutView workoutName={activeWorkoutDay} exercises={workoutTemplates[activePlanId].days[activeWorkoutDay]} onBack={() => setCurrentView('main')} onFinish={() => { setCurrentView('main'); loadUserLogs(); }} userLogs={userLogs} />;

  // If no active plan, show the Templates selection view
  if (!activePlanId) {
    return (
      <TemplatesView
        onSelectWorkout={(id) => {
          setSelectedWorkoutId(id);
          setCurrentView('detail');
        }}
        activePlanId={activePlanId || ''}
      />
    );
  }

  return (
    <div className="min-h-screen px-4 py-6 pb-40 space-y-6" style={{ backgroundColor: BG_DARK }}>
      <header className="flex items-center justify-between mb-6">
        <div><h1 className="text-2xl font-bold text-white">Treinos</h1><p className="text-[#A1A1A1] text-sm">Gerencie sua rotina</p></div>
      </header>

      {activePlanId && nextWorkoutDay && (
        <div className="space-y-6">
          <div className="w-full rounded-2xl p-6 bg-[#141414] border border-[#E53935]/30 relative overflow-hidden">
            <div className="absolute top-0 right-0 p-4 opacity-5 pointer-events-none">
              <Dumbbell className="w-32 h-32 text-white" />
            </div>
            <div className="relative z-10">
              <div className="flex justify-between items-center mb-4">
                <span className="text-[#A1A1A1] text-xs font-medium uppercase tracking-wider">
                  Hoje: <span className="text-white">{nextWorkoutDay}</span>
                </span>
                {nextNextWorkoutDay && (
                  <span className="text-[#A1A1A1] text-xs font-medium uppercase tracking-wider">
                    Próximo: <span className="text-white/60">{nextNextWorkoutDay}</span>
                  </span>
                )}
              </div>

              <h2 className="text-white font-bold text-2xl mb-1">
                {activePlanId === 'low_volume' ? (
                  nextWorkoutDay === 'Treino A' ? 'Hoje é dia de Superiores' :
                    nextWorkoutDay === 'Treino B' ? 'Hoje é dia de Inferiores' :
                      `Hoje é dia de ${nextWorkoutDay.toLowerCase()}`
                ) : isLegDay ? (
                  <>Leg Day <span className="text-[#A1A1A1] font-normal text-lg">("perna")</span></>
                ) : (
                  `Hoje é dia de ${nextWorkoutDay.toLowerCase()}`
                )}
              </h2>

              <p className="text-[#E53935] text-xs font-medium mb-6 italic">
                "{motivationPhrase}"
              </p>

              <div className="flex items-center gap-4 mb-6 text-xs text-[#A1A1A1] bg-white/5 p-3 rounded-xl border border-white/5 w-fit">
                <div className="flex items-center gap-1.5">
                  <Dumbbell className="w-3.5 h-3.5" />
                  <span>{exerciseCount} exercícios</span>
                </div>
                <div className="w-px h-3 bg-white/10" />
                <div className="flex items-center gap-1.5">
                  <Activity className="w-3.5 h-3.5" />
                  <span>42 min</span>
                </div>
                <div className="w-px h-3 bg-white/10" />
                <div className="flex items-center gap-1.5">
                  <Zap className="w-3.5 h-3.5 text-[#E53935]" />
                  <span>Intensidade: Alta</span>
                </div>
              </div>

              <button
                onClick={() => nextWorkoutDay && handleStartWorkout(nextWorkoutDay)}
                className="px-6 py-2.5 rounded-xl bg-[#E53935] text-white font-bold text-sm hover:bg-[#D32F2F] transition-colors shadow-lg shadow-red-900/20 flex items-center gap-2"
              >
                <Play className="w-4 h-4 fill-current" />
                Iniciar treino
              </button>
            </div>
          </div>

          <div className="rounded-2xl p-5 bg-[#141414] border border-white/5 space-y-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[#A1A1A1] text-xs font-medium uppercase tracking-wider mb-1">Progresso Semanal</p>
                <h3 className="text-white text-xl font-bold flex items-baseline gap-1">
                  {weekDays.filter(d => d.isDone).length}/5 <span className="text-sm font-normal text-[#A1A1A1]">treinos feitos</span>
                </h3>
                <p className="text-[#A1A1A1] text-xs mt-1">
                  {Math.round((weekDays.filter(d => d.isDone).length / 5) * 100)}% da semana completa
                </p>
              </div>
              <div className="flex items-center gap-1">
                <div className={`w-2 h-2 rounded-full ${streak > 2 ? 'bg-[#E53935]' : 'bg-[#333]'}`} />
                <div className="w-4 h-0.5 bg-[#333]" />
                <div className={`w-2 h-2 rounded-full ${streak > 1 ? 'bg-[#E53935]' : 'bg-[#333]'}`} />
                <div className="w-4 h-0.5 bg-[#333]" />
                <div className={`w-3 h-3 rounded-full border-2 ${streak > 0 ? 'border-[#E53935] bg-[#E53935]' : 'border-[#333] bg-transparent'}`} />
              </div>
            </div>

            <div className="flex justify-between items-center">
              {weekDays.map((day, idx) => (
                <div key={idx} className="flex flex-col items-center gap-2">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center border transition-all duration-300 ${day.isDone
                      ? 'bg-[#E53935] border-[#E53935] text-white'
                      : 'bg-transparent border-[#333] text-[#525252]'
                      }`}
                  >
                    {day.isDone && <Check className="w-4 h-4" />}
                  </div>
                  <span className={`text-[10px] font-medium ${day.isToday ? 'text-white' : 'text-[#525252]'}`}>
                    {day.day}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <button
            onClick={() => setActivePlanId(null)}
            className="w-full p-4 rounded-2xl bg-[#141414] border border-white/5 flex items-center justify-between group hover:bg-white/5 transition-all"
          >
            <div className="flex items-center gap-3">
              <Dumbbell className="w-5 h-5 text-[#E53935]" />
              <span className="text-white font-medium text-sm">Ver treinos completos</span>
            </div>
            <ChevronRight className="w-4 h-4 text-[#A1A1A1] group-hover:text-white transition-colors" />
          </button>
        </div>
      )}


    </div>
  );
}

function TemplatesView({ onSelectWorkout, onBack, activePlanId }: { onSelectWorkout: (id: string) => void; onBack?: () => void; activePlanId: string; }) {
  return (
    <div className="min-h-screen px-4 py-6 space-y-6" style={{ backgroundColor: BG_DARK }}>
      <header className="flex items-center gap-3 mb-4">
        {onBack && (
          <button onClick={onBack} className="p-2 rounded-lg hover:bg-white/10 transition-colors">
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
        )}
        <div>
          <h1 className="text-xl font-bold text-white leading-tight">Escolha o seu treino</h1>
          <p className="text-[#A1A1A1] text-xs mt-1">Selecione o nível que melhor representa sua experiência.</p>
        </div>
      </header>

      {/* Level Cards */}
      <div className="space-y-4 pb-6">
        {/* Beginner */}
        <div className="bg-[#141414] rounded-[18px] p-5 border border-white/10 shadow-lg flex items-start gap-4 min-h-[120px]">
          <div className="p-2 rounded-lg flex-shrink-0" style={{ backgroundColor: 'rgba(229, 57, 53, 0.1)' }}>
            <Target className="w-5 h-5 text-[#E53935]" />
          </div>
          <div>
            <h3 className="text-white font-bold text-sm mb-1">Iniciante / Amador</h3>
            <p className="text-[#A1A1A1] text-xs mb-2">Foco em aprender execução e controle.</p>
            <p className="text-[#525252] text-[10px] font-medium border-t border-white/5 pt-2">
              Recomendado: Treino ABC ou Full Body
            </p>
          </div>
        </div>

        {/* Intermediate */}
        <div className="bg-[#141414] rounded-[18px] p-5 border border-white/10 shadow-lg flex items-start gap-4 min-h-[120px]">
          <div className="p-2 rounded-lg flex-shrink-0" style={{ backgroundColor: 'rgba(229, 57, 53, 0.1)' }}>
            <TrendingUp className="w-5 h-5 text-[#E53935]" />
          </div>
          <div>
            <h3 className="text-white font-bold text-sm mb-1">Intermediário</h3>
            <p className="text-[#A1A1A1] text-xs mb-2">Pra quem já treina há algum tempo e tem boa consciência corporal.</p>
            <p className="text-[#525252] text-[10px] font-medium border-t border-white/5 pt-2">
              Recomendado: Upper/Lower ou Full Body
            </p>
          </div>
        </div>

        {/* Advanced */}
        <div className="bg-[#141414] rounded-[18px] p-5 border border-white/10 shadow-lg flex items-start gap-4 min-h-[120px]">
          <div className="p-2 rounded-lg flex-shrink-0" style={{ backgroundColor: 'rgba(229, 57, 53, 0.1)' }}>
            <Zap className="w-5 h-5 text-[#E53935]" />
          </div>
          <div>
            <h3 className="text-white font-bold text-sm mb-1">Avançado</h3>
            <p className="text-[#A1A1A1] text-xs mb-2">Pra quem domina carga, intensidade e técnica.</p>
            <p className="text-[#525252] text-[10px] font-medium border-t border-white/5 pt-2">
              Recomendado: Old School, Low Volume ou Torso Limbs
            </p>
          </div>
        </div>

        {/* Motivational Card */}
        <div className="p-4 rounded-2xl bg-[#E53935]/5 border border-[#E53935]/20 mt-4">
          <p className="text-white italic text-xs leading-relaxed">
            "Escolha o treino que mais combina com sua realidade atual.
            A evolução acontece quando você respeita seu nível."
          </p>
        </div>
      </div>

      <div className="space-y-4 pt-4 pb-6">
        {Object.entries(workoutTemplates).map(([id, template]) => {
          const IconComponent = template.icon;
          const isActive = id === activePlanId;
          return (
            <button
              key={id}
              onClick={() => onSelectWorkout(id)}
              className={`w-full p-5 rounded-[18px] border transition-all duration-300 shadow-lg group text-left min-h-[140px] ${isActive
                ? 'bg-[#141414] border-[#E53935]/50 shadow-red-900/10'
                : 'bg-[#141414] border-white/10 hover:border-white/20'}`}
              style={{ borderColor: isActive ? 'rgba(229, 57, 53, 0.5)' : BORDER_COLOR }}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg transition-colors ${isActive ? 'bg-[#E53935]/10' : 'bg-[#E53935]/10'}`}>
                    <IconComponent className={`w-5 h-5 ${isActive ? 'text-[#E53935]' : 'text-[#E53935]'}`} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className={`font-bold text-sm ${isActive ? 'text-[#E53935]' : 'text-white'}`}>{template.name}</h3>
                      {isActive && <span className="text-[10px] bg-[#E53935] text-white px-2 py-0.5 rounded-full font-bold">ATIVO</span>}
                    </div>
                  </div>
                </div>
                <ChevronRight className={`w-4 h-4 transition-colors ${isActive ? 'text-[#E53935]' : 'text-[#A1A1A1] group-hover:text-white'}`} />
              </div>

              <p className="text-[#A1A1A1] text-xs leading-relaxed pl-1">{template.description}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function WorkoutDetail({
  workout,
  workoutId,
  isActive,
  onActivate,
  onBack,
  userLogs
}: {
  workout: WorkoutPlan;
  workoutId: string;
  isActive: boolean;
  onActivate: (id: string) => void;
  onBack: () => void;
  userLogs: UserWorkoutLog[];
}) {
  const getExerciseHistory = (exerciseName: string) => {
    return userLogs.filter(log => log.exercise_name === exerciseName).slice(0, 3);
  };

  return (
    <div className="min-h-screen px-4 py-6 space-y-6" style={{ backgroundColor: BG_DARK }}>
      <header className="flex items-center gap-3 mb-6">
        <button onClick={onBack} className="p-2 rounded-lg hover:bg-white/10 transition-colors">
          <ArrowLeft className="w-5 h-5 text-white" />
        </button>
        <h1 className="text-xl font-bold text-white">{workout.name}</h1>
      </header>

      {!isActive && (
        <button
          onClick={() => {
            onActivate(workoutId);
            onBack();
          }}
          className="w-full py-3 rounded-xl bg-[#E53935] text-white font-bold text-sm hover:bg-[#C62828] transition-colors shadow-lg shadow-red-900/20 flex items-center justify-center gap-2"
        >
          <Check className="w-4 h-4" />
          Definir como meu treino atual
        </button>
      )}

      {isActive && (
        <div className="w-full py-3 rounded-xl bg-[#1A1A1A] border border-[#E53935]/30 text-[#E53935] font-bold text-sm flex items-center justify-center gap-2">
          <CheckCircle2 className="w-4 h-4" />
          Este é seu plano atual
        </div>
      )}

      <div className="rounded-2xl p-5 bg-[#141414] border border-[#E53935]/20 relative overflow-hidden">
        <div className="flex items-start gap-3 relative z-10">
          <div className="p-2 rounded-lg bg-[#E53935]/10 text-[#E53935]">
            <Target className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-white font-bold text-sm mb-1">Consistência é a chave</h3>
            <p className="text-[#A1A1A1] text-xs leading-relaxed">
              Não mude seu treino por 30 dias, observe como seu corpo reage, adapte-se ao processo e só então avalie mudanças.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        <h2 className="text-lg font-bold text-white">Exercícios</h2>

        {Object.entries(workout.days).map(([dayName, exercises]) => (
          <div key={dayName} className="space-y-3 pb-4">
            <h3 className="text-[#E53935] font-bold text-sm uppercase tracking-wide pl-1">{dayName}</h3>
            <div className="space-y-2">
              {exercises.map((exercise, index) => {
                const history = getExerciseHistory(exercise.name);
                return (
                  <div key={`${dayName}-${exercise.name}-${index}`} className="rounded-2xl p-4 bg-[#141414] border hover:border-[#E53935]/50 transition-colors min-h-[100px]" style={{ borderColor: BORDER_COLOR }}>
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <h4 className="text-white font-bold">{exercise.name}</h4>
                        <p className="text-[#A1A1A1] text-xs uppercase tracking-wider">{exercise.muscle_group}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-white text-sm font-bold">{exercise.sets} séries</p>
                        <p className="text-[#A1A1A1] text-sm">{exercise.reps} reps</p>
                      </div>
                    </div>
                    {exercise.notes && (
                      <div className="mt-2 p-2 rounded-lg bg-white/5 border border-white/5">
                        <p className="text-[#A1A1A1] text-xs italic">{exercise.notes}</p>
                      </div>
                    )}
                    {history.length > 0 && (
                      <div className="mt-3 p-2 rounded-lg bg-[#0D0D0D] border border-white/5">
                        <p className="text-[#E53935] text-xs font-bold mb-1 flex items-center gap-1">
                          <TrendingUp className="w-3 h-3" /> Últimas cargas:
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {history.map((log, i) => (
                            <span key={i} className="text-xs px-2 py-1 bg-white/10 rounded text-white">
                              {log.load}kg
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function LoadHistory({ onBack, historicoCargas, setHistoricoCargas, user }: { onBack: () => void; historicoCargas: HistoricoCarga[]; setHistoricoCargas: React.Dispatch<React.SetStateAction<HistoricoCarga[]>>; user: User | null; }) {
  const handleDeleteCarga = async (id: string) => {
    if (!user) return;
    try {
      const { error } = await supabase.from('user_workout_logs').delete().eq('id', id);
      if (error) throw error;
      setHistoricoCargas(prev => prev.filter(c => c.id !== id));
      try {
        const storageKey = `historico_cargas_${user.id}`;
        const currentData = JSON.parse(localStorage.getItem(storageKey) || '[]');
        const updatedData = currentData.filter((carga: HistoricoCarga) => carga.id !== id);
        localStorage.setItem(storageKey, JSON.stringify(updatedData));
      } catch (error) {
        console.error('Erro ao atualizar localStorage:', error);
      }
    } catch (error) {
      console.error('Erro ao excluir carga:', error);
    }
  };

  const sortedLogs = [...historicoCargas].sort((a, b) => {
    const dateA = new Date(a.data.split('/').reverse().join('-') + ' ' + a.horario);
    const dateB = new Date(b.data.split('/').reverse().join('-') + ' ' + b.horario);
    return dateB.getTime() - dateA.getTime();
  });

  return (
    <div className="min-h-screen px-4 py-6 space-y-6" style={{ backgroundColor: BG_DARK }}>
      <header className="flex items-center gap-3 mb-6">
        <button onClick={onBack} className="p-2 rounded-lg hover:bg-white/10 transition-colors">
          <ArrowLeft className="w-5 h-5 text-white" />
        </button>
        <h1 className="text-xl font-bold text-white">Histórico de Cargas</h1>
      </header>

      {sortedLogs.length === 0 ? (
        <div className="text-center py-12">
          <div className="p-6 rounded-2xl bg-[#141414] border" style={{ borderColor: BORDER_COLOR }}>
            <TrendingUp className="w-12 h-12 text-[#A1A1A1] mx-auto mb-4" />
            <h3 className="text-white font-medium mb-2">Nenhuma carga registrada</h3>
            <p className="text-[#A1A1A1] text-sm">Comece a registrar suas cargas para acompanhar sua evolução.</p>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="space-y-3">
            {sortedLogs.map((registro) => (
              <div key={registro.id} className="p-4 rounded-xl bg-[#141414] border hover:bg-white/5 transition-colors" style={{ borderColor: BORDER_COLOR }}>
                <div className="flex justify-between items-start mb-3">
                  <div className="text-white font-bold text-sm">{registro.exercicio}</div>
                  <div className="text-[#E53935] text-sm font-bold">{registro.carga}kg</div>
                </div>
                <div className="grid grid-cols-2 gap-4 text-xs">
                  <div>
                    <div className="text-[#A1A1A1]">Repetições</div>
                    <div className="text-white">{registro.repeticoes}</div>
                  </div>
                  <div>
                    <div className="text-[#A1A1A1]">Séries</div>
                    <div className="text-white">{registro.series}</div>
                  </div>
                  <div>
                    <div className="text-[#A1A1A1]">Data</div>
                    <div className="text-white">{registro.data}</div>
                  </div>
                  <div>
                    <div className="text-[#A1A1A1]">Horário</div>
                    <div className="text-white">{registro.horario}</div>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-white/10">
                  <button
                    onClick={() => handleDeleteCarga(registro.id)}
                    className="flex items-center gap-2 text-red-400 hover:text-red-300 text-xs font-medium transition-colors"
                  >
                    <Trash2 className="w-3 h-3" />
                    Excluir
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function RegisterLoad({ onBack, onNavigateToHistory, setHistoricoCargas }: { onBack: () => void; onNavigateToHistory: () => void; setHistoricoCargas: React.Dispatch<React.SetStateAction<HistoricoCarga[]>>; }) {
  const { user } = useAuth();
  const [exerciseName, setExerciseName] = useState('');
  const [load, setLoad] = useState('');
  const [reps, setReps] = useState('');
  const [sets, setSets] = useState('');
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const getCurrentDateTime = () => {
    const now = new Date();
    const date = now.toLocaleDateString('pt-BR');
    const time = now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    return { date, time };
  };

  const { date, time } = getCurrentDateTime();

  const handleRegisterLoad = async () => {
    if (!user || !exerciseName || !load || !reps || !sets) return;

    try {
      setLoading(true);

      const novoRegistro: HistoricoCarga = {
        id: Date.now().toString(),
        exercicio: exerciseName,
        carga: parseFloat(load),
        repeticoes: parseInt(reps),
        series: parseInt(sets),
        data: date,
        horario: time
      };

      setHistoricoCargas(prev => [novoRegistro, ...prev]);

      try {
        const storageKey = `historico_cargas_${user.id}`;
        const currentData = JSON.parse(localStorage.getItem(storageKey) || '[]');
        const updatedData = [novoRegistro, ...currentData];
        localStorage.setItem(storageKey, JSON.stringify(updatedData));
      } catch (error) {
        console.error('Erro ao salvar no localStorage:', error);
      }

      await supabase.from('user_workout_logs').insert({
        user_id: user.id,
        exercise_name: exerciseName,
        load: parseFloat(load),
        reps: parseInt(reps),
        sets: parseInt(sets),
        date: new Date().toISOString(),
        date_formatted: date,
        time_formatted: time
      });

      setExerciseName('');
      setLoad('');
      setReps('');
      setSets('');
      setSuccessMessage('Carga registrada com sucesso!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Erro ao registrar carga:', error);
      alert('Erro ao registrar carga. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen px-4 py-6 space-y-6" style={{ backgroundColor: BG_DARK }}>
      <header className="flex items-center gap-3 mb-6">
        <button onClick={onBack} className="p-2 rounded-lg hover:bg-white/10 transition-colors">
          <ArrowLeft className="w-5 h-5 text-white" />
        </button>
        <h1 className="text-xl font-bold text-white">Registrar Cargas</h1>
      </header>

      {successMessage && (
        <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/20 text-green-400 text-sm font-medium flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4" />
          {successMessage}
        </div>
      )}

      <div className="space-y-4">
        {/* Date and Time Display */}
        <div className="bg-[#141414] rounded-xl border p-4 flex justify-between items-center" style={{ borderColor: BORDER_COLOR }}>
          <div>
            <label className="block text-[#A1A1A1] text-xs font-medium mb-1">Data</label>
            <div className="text-white text-sm font-medium">{date}</div>
          </div>
          <div className="text-right">
            <label className="block text-[#A1A1A1] text-xs font-medium mb-1">Horário</label>
            <div className="text-white text-sm font-medium">{time}</div>
          </div>
        </div>

        <div>
          <label className="block text-[#A1A1A1] text-xs font-medium mb-2">Exercício</label>
          <input
            type="text"
            value={exerciseName}
            onChange={(e) => setExerciseName(e.target.value)}
            placeholder="Ex: Supino reto"
            className="w-full bg-[#141414] text-white rounded-xl border px-4 py-3 focus:outline-none focus:border-[#E53935] transition-colors"
            style={{ borderColor: BORDER_COLOR }}
          />
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-[#A1A1A1] text-xs font-medium mb-2">Carga (kg)</label>
            <input
              type="number"
              value={load}
              onChange={(e) => setLoad(e.target.value)}
              placeholder="0"
              className="w-full bg-[#141414] text-white rounded-xl border px-4 py-3 focus:outline-none focus:border-[#E53935] transition-colors"
              style={{ borderColor: BORDER_COLOR }}
            />
          </div>
          <div>
            <label className="block text-[#A1A1A1] text-xs font-medium mb-2">Reps</label>
            <input
              type="number"
              value={reps}
              onChange={(e) => setReps(e.target.value)}
              placeholder="0"
              className="w-full bg-[#141414] text-white rounded-xl border px-4 py-3 focus:outline-none focus:border-[#E53935] transition-colors"
              style={{ borderColor: BORDER_COLOR }}
            />
          </div>
          <div>
            <label className="block text-[#A1A1A1] text-xs font-medium mb-2">Séries</label>
            <input
              type="number"
              value={sets}
              onChange={(e) => setSets(e.target.value)}
              placeholder="0"
              className="w-full bg-[#141414] text-white rounded-xl border px-4 py-3 focus:outline-none focus:border-[#E53935] transition-colors"
              style={{ borderColor: BORDER_COLOR }}
            />
          </div>
        </div>

        <button
          onClick={handleRegisterLoad}
          disabled={loading || !exerciseName || !load}
          className="w-full py-3 rounded-lg bg-[#E53935] text-white font-bold text-sm hover:bg-[#C62828] transition-colors disabled:opacity-50 disabled:cursor-not-allowed mt-4"
        >
          {loading ? 'Salvando...' : 'Registrar Carga'}
        </button>

        <button
          onClick={onNavigateToHistory}
          className="w-full py-3 rounded-lg bg-[#141414] border border-white/10 text-white font-medium text-sm hover:bg-white/5 transition-colors flex items-center justify-center gap-2"
        >
          Acompanhe seu histórico de cargas
        </button>
      </div>
    </div>
  );
}

function IndividualWorkoutView({
  workoutName,
  exercises,
  onBack,
  onFinish,
  userLogs
}: {
  workoutName: string;
  exercises: WorkoutExercise[];
  onBack: () => void;
  onFinish: () => void;
  userLogs: UserWorkoutLog[];
}) {
  const { user } = useAuth();
  const { addXP } = useGamification();
  const [started, setStarted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [completedExercises, setCompletedExercises] = useState<string[]>([]);
  const [expandedExercise, setExpandedExercise] = useState<string | null>(null);
  const [sessionLogs, setSessionLogs] = useState<UserWorkoutLog[]>([]);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [canFinish, setCanFinish] = useState(false);

  // Form state
  const [loadInput, setLoadInput] = useState('');
  const [repsInput, setRepsInput] = useState('');

  useEffect(() => {
    if (started && !startTime) {
      setStartTime(Date.now());
    }
  }, [started, startTime]);

  useEffect(() => {
    if (!startTime) return;
    // Check every minute
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      if (elapsed >= 45 * 60 * 1000) { // 45 minutes
        setCanFinish(true);
      }
    }, 60000);

    // Initial check
    if (Date.now() - startTime >= 45 * 60 * 1000) {
      setCanFinish(true);
    }

    return () => clearInterval(interval);
  }, [startTime]);

  const handleToggleExpand = (name: string) => {
    if (expandedExercise === name) {
      setExpandedExercise(null);
    } else {
      setExpandedExercise(name);
      setLoadInput('');
      setRepsInput('');
    }
  };

  const handleRegisterLoad = async (exercise: WorkoutExercise) => {
    if (!user || !loadInput || !repsInput) return;

    try {
      const now = new Date();
      const date = now.toLocaleDateString('pt-BR');
      const time = now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

      // Create a temporary ID for the session log
      const tempId = Date.now().toString();

      const novoRegistro: UserWorkoutLog = {
        id: tempId,
        user_id: user.id,
        exercise_name: exercise.name,
        load: parseFloat(loadInput),
        reps: parseInt(repsInput),
        sets: exercise.sets,
        date: now.toISOString(),
        date_formatted: date,
        time_formatted: time
      };

      // Optimistic update
      setSessionLogs(prev => [novoRegistro, ...prev]);

      if (!completedExercises.includes(exercise.name)) {
        setCompletedExercises(prev => [...prev, exercise.name]);
      }

      // Async save
      const { error } = await supabase.from('user_workout_logs').insert({
        user_id: user.id,
        exercise_name: exercise.name,
        load: parseFloat(loadInput),
        reps: parseInt(repsInput),
        sets: exercise.sets,
        date: now.toISOString(),
        date_formatted: date,
        time_formatted: time
      });

      if (error) throw error;

      setLoadInput('');
      setRepsInput('');

    } catch (error) {
      console.error('Erro ao registrar carga:', error);
      alert('Erro ao salvar carga. Tente novamente.');
    }
  };

  const getExerciseHistory = (name: string) => {
    // Combine session logs with historical logs, filtering by exercise name
    const allLogs = [...sessionLogs, ...userLogs];
    return allLogs
      .filter(log => log.exercise_name === name)
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .slice(0, 5);
  };

  const handleFinish = async () => {
    if (!canFinish || !user) return;
    setLoading(true);

    try {
      const today = new Date().toISOString().split('T')[0];

      // 1. Fetch existing progress to avoid overwriting other fields
      const { data: existing } = await supabase
        .from('daily_progress')
        .select('*')
        .eq('user_id', user.id)
        .eq('date', today)
        .maybeSingle();

      // Preparar dados para cálculo de pontos
      const progressData = existing
        ? {
            workout_completed: true,
            sleep_hours: existing.sleep_hours,
            meals_logged: existing.meals_logged || 0
          }
        : {
            workout_completed: true,
            sleep_hours: null,
            meals_logged: 0
          };

      // Calcular pontos e flags de conclusão
      const mealsCompleted = isMealsCompleted(progressData.meals_logged);
      const sleepCompleted = isSleepCompleted(progressData.sleep_hours);
      const points = calculateDailyPoints(progressData);

      const updates = existing
        ? {
            ...existing,
            workout_completed: true,
            meals_completed: mealsCompleted,
            sleep_completed: sleepCompleted,
            points: points
          }
        : {
            user_id: user.id,
            date: today,
            workout_completed: true,
            water_ml: 0,
            meals_logged: 0,
            meals_completed: mealsCompleted,
            sleep_completed: sleepCompleted,
            points: points
          };

      // 2. Upsert
      const { error } = await supabase
        .from('daily_progress')
        .upsert(updates);

      if (error) {
        console.error('Erro ao salvar progresso do treino:', error);
        console.error('Detalhes do erro:', {
          message: error.message,
          code: (error as any).code,
          details: (error as any).details,
          hint: (error as any).hint,
          updates: updates
        });
        throw error;
      }
      
      console.log('Progresso do treino salvo com sucesso:', updates);

      // Disparar evento para atualizar dashboard e pontuação ofensiva
      window.dispatchEvent(new CustomEvent('dailyProgressUpdated'));

      await addXP(10);

      await new Promise(resolve => setTimeout(resolve, 1000));
      onFinish();
    } catch (err) {
      console.error('Error finishing workout:', err);
      alert('Erro ao salvar progresso. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen px-4 py-6 space-y-6" style={{ backgroundColor: BG_DARK }}>
      {/* Header */}
      <header className="flex items-center gap-3 mb-2">
        <button onClick={onBack} className="p-2 rounded-lg hover:bg-white/10 transition-colors">
          <ArrowLeft className="w-5 h-5 text-white" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-white">{workoutName}</h1>
          <p className="text-[#A1A1A1] text-xs">Treino do dia</p>
        </div>
      </header>

      {/* Start Button - Matches Main Tab Style */}
      <button
        onClick={() => setStarted(true)}
        className={`w-full py-3 rounded-xl font-bold text-sm transition-all shadow-lg flex items-center justify-center gap-2 ${started
          ? 'bg-[#1A1A1A] text-[#E53935] border border-[#E53935]/30'
          : 'bg-[#E53935] text-white hover:bg-[#D32F2F] shadow-red-900/20'
          }`}
      >
        {started ? (
          <>
            <Activity className="w-4 h-4 animate-pulse" />
            Treino em Andamento
          </>
        ) : (
          <>
            <Play className="w-4 h-4 fill-current" />
            Iniciar treino
          </>
        )}
      </button>

      {/* Exercises List */}
      <div className="space-y-4">
        {exercises.map((exercise, index) => {
          const isCompleted = completedExercises.includes(exercise.name);
          const isExpanded = expandedExercise === exercise.name;
          const history = getExerciseHistory(exercise.name);

          return (
            <div key={index} className={`rounded-2xl overflow-hidden transition-all duration-300 ${isCompleted ? 'bg-[#141414] border border-[#E53935]/30' : 'bg-[#141414] border border-white/5'
              }`}>
              {/* Card Header / Main Content */}
              <div className="p-5">
                <div className="flex gap-4">
                  {/* Icon - Standardized to Goals Tab Style */}
                  <div className="p-2 rounded-lg flex-shrink-0 h-fit" style={{ backgroundColor: 'rgba(229, 57, 53, 0.1)' }}>
                    <Dumbbell className="w-5 h-5 text-[#E53935]" />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className={`font-bold text-base mb-1 ${isCompleted ? 'text-[#E53935]' : 'text-white'}`}>
                      {exercise.name}
                    </h3>
                    <p className="text-[#A1A1A1] text-sm mb-3">
                      {exercise.sets} séries × {exercise.reps}
                    </p>

                    {/* Suggestions */}
                    {exercise.suggestions && exercise.suggestions.length > 0 && (
                      <div className="mb-3">
                        <p className="text-[#A1A1A1] text-[10px] mb-1.5 opacity-80">Você pode fazer variações deste exercício:</p>
                        <div className="flex flex-wrap gap-2">
                          {exercise.suggestions.map((sug, i) => (
                            <span key={i} className="text-[10px] px-2 py-1 rounded bg-[#E53935]/10 text-[#E53935] border border-[#E53935]/20">
                              {sug}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Tips */}
                    {exercise.tips && exercise.tips.length > 0 && (
                      <div className="mb-1 pl-3 border-l-2 border-[#E53935]/30 space-y-1">
                        {exercise.tips.slice(0, 3).map((tip, i) => (
                          <p key={i} className="text-[11px] text-[#A1A1A1] italic leading-tight">
                            "{tip}"
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Button - Centered in Card */}
                <button
                  onClick={() => handleToggleExpand(exercise.name)}
                  className={`w-full mt-4 py-3 rounded-xl font-bold text-xs transition-all shadow-lg flex items-center justify-center gap-2 ${isExpanded
                    ? 'bg-[#1A1A1A] text-[#E53935] border border-[#E53935]/30'
                    : 'bg-[#E53935] text-white hover:bg-[#D32F2F] shadow-red-900/20'
                    }`}
                >
                  {isExpanded ? (
                    <>Fechar Registro</>
                  ) : (
                    <>
                      <TrendingUp className="w-3.5 h-3.5" />
                      Registrar Cargas
                    </>
                  )}
                </button>
              </div>

              {/* Expandable Load Registration Area */}
              {isExpanded && (
                <div className="bg-[#0A0A0A] border-t border-white/5 p-5 animate-in slide-in-from-top-2 duration-200">
                  <div className="space-y-4">
                    <div className="flex gap-3">
                      <div className="flex-1">
                        <label className="block text-[#A1A1A1] text-[10px] font-medium mb-1.5 uppercase tracking-wider">Carga (kg)</label>
                        <input
                          type="number"
                          value={loadInput}
                          onChange={(e) => setLoadInput(e.target.value)}
                          placeholder="0"
                          className="w-full bg-[#141414] text-white rounded-lg border border-white/10 px-3 py-2.5 text-sm focus:outline-none focus:border-[#E53935] transition-colors"
                        />
                      </div>
                      <div className="flex-1">
                        <label className="block text-[#A1A1A1] text-[10px] font-medium mb-1.5 uppercase tracking-wider">Repetições</label>
                        <input
                          type="number"
                          value={repsInput}
                          onChange={(e) => setRepsInput(e.target.value)}
                          placeholder="0"
                          className="w-full bg-[#141414] text-white rounded-lg border border-white/10 px-3 py-2.5 text-sm focus:outline-none focus:border-[#E53935] transition-colors"
                        />
                      </div>
                    </div>

                    <button
                      onClick={() => handleRegisterLoad(exercise)}
                      disabled={!loadInput || !repsInput}
                      className="w-full py-3 rounded-lg bg-[#E53935] text-white font-bold text-xs hover:bg-[#C62828] transition-colors shadow-lg shadow-red-900/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      SALVAR SÉRIE
                    </button>

                    {/* Embedded History */}
                    {history.length > 0 && (
                      <div className="pt-4 border-t border-white/5">
                        <p className="text-[#A1A1A1] text-[10px] font-medium mb-2 uppercase tracking-wider">Histórico Recente</p>
                        <div className="space-y-2">
                          {history.map((log, i) => (
                            <div key={i} className="flex justify-between items-center text-xs p-2 rounded bg-white/5">
                              <span className="text-white font-medium">{log.load}kg</span>
                              <span className="text-[#A1A1A1]">{log.reps} reps</span>
                              <span className="text-[#525252] text-[10px]">{new Date(log.date).toLocaleDateString('pt-BR')}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Finish Workout Button */}
      {started && (
        <div className="pt-6 border-t border-white/5">
          <button
            onClick={handleFinish}
            disabled={!canFinish || loading}
            className={`w-full py-4 rounded-xl font-bold text-sm transition-colors shadow-lg flex items-center justify-center gap-2 ${canFinish
              ? 'bg-white text-black hover:bg-gray-200'
              : 'bg-[#1A1A1A] text-[#525252] cursor-not-allowed border border-white/5'
              }`}
          >
            {loading ? (
              <Activity className="w-5 h-5 animate-spin" />
            ) : (
              <Award className="w-5 h-5" />
            )}
            {loading ? 'Finalizando...' : 'Finalizar Treino'}
          </button>
          {canFinish && !loading && (
            <p className="text-center text-[#E53935] text-[10px] mt-2">
              +10 XP ao concluir
            </p>
          )}
          {!canFinish && (
            <p className="text-center text-[#525252] text-[10px] mt-2">
              Disponível após 45 min de treino
            </p>
          )}
        </div>
      )}
    </div>
  );
}

